const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper para manejar respuestas
const handleResponse = async (response) => {
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Error en la petición');
    }
    return data;
};

// Helper para obtener headers con token
const getHeaders = () => {
    // Buscar token en ambos storages (localStorage para "recordarme", sessionStorage para sesión normal)
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

// ============ AUTH ============
export const authAPI = {
    login: async (email, password, rememberMe = false) => {
        const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, rememberMe })
        });
        return handleResponse(response);
    },

    getProfile: async () => {
        const response = await fetch(`${API_URL}/auth/profile`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    updateProfile: async (nombre) => {
        const response = await fetch(`${API_URL}/auth/profile`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ nombre })
        });
        return handleResponse(response);
    },

    changePassword: async (newPassword) => {
        const response = await fetch(`${API_URL}/auth/change-password`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ newPassword })
        });
        return handleResponse(response);
    }
};

// ============ USUARIOS ============
export const usuariosAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/usuarios`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/usuarios/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/usuarios`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/usuarios/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    resetPassword: async (id, newPassword) => {
        const response = await fetch(`${API_URL}/usuarios/${id}/reset-password`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ newPassword })
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/usuarios/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ CATEGORÍAS DE SERVICIO ============
export const categoriasAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/categorias?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/categorias/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/categorias`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/categorias/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/categorias/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ TIPOS DE SERVICIO ============
export const tiposServicioAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/tipos-servicio?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getGrouped: async () => {
        const response = await fetch(`${API_URL}/tipos-servicio/grouped`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/tipos-servicio/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/tipos-servicio`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/tipos-servicio/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/tipos-servicio/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ PERSONAL ============
export const personalAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/personal?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/personal/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/personal`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/personal/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/personal/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ CATEGORÍAS DE COSTO FIJO ============
export const categoriasCostoFijoAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/categorias-costo-fijo?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ COSTOS FIJOS ============
export const costosFijosAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/costos-fijos?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getGrouped: async () => {
        const response = await fetch(`${API_URL}/costos-fijos/grouped`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/costos-fijos`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ CONFIGURACION ============
export const configuracionAPI = {
    // Endpoint público (sin autenticación) - para Login
    getPublicLogo: async () => {
        const response = await fetch(`${API_URL}/configuracion/public/logo`);
        return handleResponse(response);
    },

    getAll: async () => {
        const response = await fetch(`${API_URL}/configuracion`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getByKey: async (clave) => {
        const response = await fetch(`${API_URL}/configuracion/${clave}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    update: async (clave, valor) => {
        const response = await fetch(`${API_URL}/configuracion/${clave}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ valor })
        });
        return handleResponse(response);
    },

    updateMultiple: async (configs) => {
        const response = await fetch(`${API_URL}/configuracion/batch`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(configs)
        });
        return handleResponse(response);
    },

    getTipoCambioSunat: async () => {
        const response = await fetch(`${API_URL}/configuracion/tipo-cambio/sunat`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getTipoCambioAPI: async () => {
        const response = await fetch(`${API_URL}/configuracion/tipo-cambio/api`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getNextCotizacionNumber: async () => {
        const response = await fetch(`${API_URL}/configuracion/next-cotizacion-number`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ COTIZACIONES ============
export const cotizacionesAPI = {
    getAll: async (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetch(
            `${API_URL}/cotizaciones${queryParams ? `?${queryParams}` : ''}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getMyCotizaciones: async () => {
        const response = await fetch(`${API_URL}/cotizaciones/mis-cotizaciones`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/cotizaciones`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    updateEstado: async (id, estado) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/estado`, {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify({ estado })
        });
        return handleResponse(response);
    },

    duplicate: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/duplicate`, {
            method: 'POST',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    createVersion: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/version`, {
            method: 'POST',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// API de Plantillas
export const plantillasAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/plantillas`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/plantillas/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/plantillas`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/plantillas/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/plantillas/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// API de Clientes
export const clientesAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/clientes`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getWithStats: async (params = {}) => {
        // Filter out undefined values before creating URL params
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        );
        const queryParams = new URLSearchParams(filteredParams).toString();
        const response = await fetch(`${API_URL}/clientes/with-stats${queryParams ? `?${queryParams}` : ''}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    search: async (query) => {
        const response = await fetch(`${API_URL}/clientes/search?q=${encodeURIComponent(query || '')}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/clientes/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getCotizaciones: async (id, estado = null) => {
        const params = estado ? `?estado=${estado}` : '';
        const response = await fetch(`${API_URL}/clientes/${id}/cotizaciones${params}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/clientes`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/clientes/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/clientes/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ UPLOAD ============
export const uploadAPI = {
    uploadLogo: async (file) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const formData = new FormData();
        formData.append('logo', file);

        const response = await fetch(`${API_URL}/upload/logo`, {
            method: 'POST',
            headers: {
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: formData
        });
        const result = await handleResponse(response);
        // Asegurar que solo se guarde la ruta relativa
        if (result.url && result.url.startsWith('http')) {
            try {
                result.url = new URL(result.url).pathname;
            } catch (e) { /* mantener url original */ }
        }
        return result;
    },

    deleteFile: async (filename) => {
        const response = await fetch(`${API_URL}/upload/${filename}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ DASHBOARD ============
export const dashboardAPI = {
    getStats: async (params = {}) => {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        );
        const queryParams = new URLSearchParams(filteredParams).toString();
        const response = await fetch(`${API_URL}/dashboard/stats${queryParams ? `?${queryParams}` : ''}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ ALERTAS ============
export const alertasAPI = {
    // Tipos de actividad
    getTiposActividad: async () => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad`, { headers: getHeaders() });
        return handleResponse(response);
    },
    createTipoActividad: async (data) => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    updateTipoActividad: async (id, data) => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad/${id}`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    deleteTipoActividad: async (id) => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad/${id}`, {
            method: 'DELETE', headers: getHeaders()
        });
        return handleResponse(response);
    },

    // Config alertas (umbrales y colores)
    getConfig: async () => {
        const response = await fetch(`${API_URL}/alertas/config`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateConfig: async (id, data) => {
        const response = await fetch(`${API_URL}/alertas/config/${id}`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    // SMTP
    getSmtp: async () => {
        const response = await fetch(`${API_URL}/alertas/smtp`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateSmtp: async (data) => {
        const response = await fetch(`${API_URL}/alertas/smtp`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    testSmtp: async (email) => {
        const response = await fetch(`${API_URL}/alertas/smtp/test`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify({ email })
        });
        return handleResponse(response);
    },

    // Email templates
    getTemplates: async () => {
        const response = await fetch(`${API_URL}/alertas/templates`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateTemplate: async (id, data) => {
        const response = await fetch(`${API_URL}/alertas/templates/${id}`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    // Slack
    getSlack: async () => {
        const response = await fetch(`${API_URL}/alertas/slack`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateSlack: async (data) => {
        const response = await fetch(`${API_URL}/alertas/slack`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    testSlack: async () => {
        const response = await fetch(`${API_URL}/alertas/slack/test`, {
            method: 'POST', headers: getHeaders()
        });
        return handleResponse(response);
    },

    // Actividades de cotización
    getActividades: async (cotizacionId) => {
        const response = await fetch(`${API_URL}/alertas/cotizacion/${cotizacionId}/actividades`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    registrarActividad: async (cotizacionId, data) => {
        const response = await fetch(`${API_URL}/alertas/cotizacion/${cotizacionId}/actividades`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    }
};

export const permisosAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/permisos`, { headers: getHeaders() });
        return handleResponse(response);
    },
    update: async (rol, permisos) => {
        const response = await fetch(`${API_URL}/permisos`, {
            method: 'PUT', headers: getHeaders(),
            body: JSON.stringify({ rol, permisos })
        });
        return handleResponse(response);
    },
};

const api = {
    auth: authAPI,
    usuarios: usuariosAPI,
    categorias: categoriasAPI,
    tiposServicio: tiposServicioAPI,
    personal: personalAPI,
    categoriasCostoFijo: categoriasCostoFijoAPI,
    costosFijos: costosFijosAPI,
    configuracion: configuracionAPI,
    cotizaciones: cotizacionesAPI,
    plantillas: plantillasAPI,
    clientes: clientesAPI,
    upload: uploadAPI,
    dashboard: dashboardAPI,
    alertas: alertasAPI,
    permisos: permisosAPI
};

export default api;
